import { useState } from "react";

import type { City, Country, GeoState as State } from "../api/types";
import { CrudPage } from "../components/CrudPage";
import { Bool } from "../components/ui";

/**
 * Countries → states → cities (02 §16). Cities carry a centre so customer
 * addresses resolve to a city and parcel service areas can be checked.
 */
export function GeoPage() {
  const [country, setCountry] = useState<{ id: string; name: string } | null>(null);
  const [state, setState] = useState<{ id: string; name: string } | null>(null);

  return (
    <>
      <CrudPage<Country>
        title="Countries"
        resource="geo/countries"
        paged={false}
        canDelete={false}
        columns={[
          { key: "name", label: "Country" },
          { key: "iso2", label: "ISO", render: (c) => <span className="mono">{c.iso2}{c.iso3 ? ` / ${c.iso3}` : ""}</span> },
          { key: "phone_code", label: "Phone", render: (c) => c.phone_code ?? "—" },
          { key: "currency_code", label: "Currency", render: (c) => c.currency_code ?? "—" },
          { key: "is_active", label: "Active", render: (c) => <Bool value={c.is_active} /> },
        ]}
        fields={[
          { name: "name", label: "Name", required: true },
          { name: "iso2", label: "ISO-2", required: true, hint: "Two letters, e.g. IN" },
          { name: "iso3", label: "ISO-3" },
          { name: "phone_code", label: "Phone code", placeholder: "+91" },
          { name: "currency_code", label: "Currency", placeholder: "INR" },
          { name: "is_active", label: "Active", type: "switch" },
        ]}
        rowActions={(c) => (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => (setCountry({ id: c.id, name: c.name }), setState(null))}>
            States
          </button>
        )}
      />
      {country && (
        <section className="card" style={{ marginTop: 24 }}>
          <CrudPage<State>
            key={country.id}
            title={`States · ${country.name}`}
            resource="geo/states"
            listQuery={{ country_id: country.id }}
            paged={false}
            canDelete={false}
            columns={[
              { key: "name", label: "State" },
              { key: "code", label: "Code", render: (s) => s.code ?? "—" },
              { key: "is_active", label: "Active", render: (s) => <Bool value={s.is_active} /> },
            ]}
            fields={[
              { name: "name", label: "Name", required: true },
              { name: "code", label: "Code", placeholder: "TN" },
              { name: "is_active", label: "Active", type: "switch" },
            ]}
            toBody={(v) => ({ ...v, country_id: country.id })}
            rowActions={(s) => (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setState({ id: s.id, name: s.name })}>
                Cities
              </button>
            )}
          />
        </section>
      )}
      {state && (
        <section className="card" style={{ marginTop: 24 }}>
          <CrudPage<City>
            key={state.id}
            title={`Cities · ${state.name}`}
            resource="geo/cities"
            listQuery={{ state_id: state.id }}
            paged={false}
            canDelete={false}
            columns={[
              { key: "name", label: "City" },
              { key: "lat", label: "Centre", render: (c) => (c.lat == null ? <span className="muted">no centre</span> : <span className="mono">{c.lat.toFixed(4)}, {c.lng?.toFixed(4)}</span>) },
              { key: "is_active", label: "Active", render: (c) => <Bool value={c.is_active} /> },
            ]}
            fields={[
              { name: "name", label: "Name", required: true },
              { name: "lat", label: "Latitude", type: "number", hint: "Centre; addresses within 50 km resolve to this city" },
              { name: "lng", label: "Longitude", type: "number" },
              { name: "is_active", label: "Active", type: "switch" },
            ]}
            toBody={(v) => ({ ...v, state_id: state.id })}
          />
        </section>
      )}
    </>
  );
}
